import React, { useState } from 'react';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Brain,
  Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useLearnings,
  useCreateLearning,
  useUpdateLearning,
  useDeleteLearning,
} from '../hooks/useExpertLearnings';
import { useTradingSkills } from '../hooks/useTradingAnalysis';
import { tradingApi } from '../services/tradingApi';
import type { ExpertLearning, LearningFilters } from '../types/expert';
import type { SkillContent } from '../types/trading';

const PAGE_SIZE = 20;

const levelBadge: Record<string, string> = {
  user: 'bg-purple-500/10 text-purple-400',
  domain: 'bg-blue-500/10 text-blue-400',
  symbol: 'bg-teal-500/10 text-teal-400',
  strategy: 'bg-amber-500/10 text-amber-400',
};

const statusBadge: Record<string, string> = {
  auto_approved: 'bg-emerald-500/10 text-emerald-400',
  admin_approved: 'bg-teal-500/10 text-teal-400',
  pending: 'bg-amber-500/10 text-amber-400',
  rejected: 'bg-red-500/10 text-red-400',
};

const sourceBadge: Record<string, string> = {
  chat: 'bg-indigo-500/10 text-indigo-400',
  trade_review: 'bg-blue-500/10 text-blue-400',
  strategy_health: 'bg-orange-500/10 text-orange-400',
  contra: 'bg-orange-500/10 text-orange-400',
  manual: 'bg-slate-500/10 text-slate-400',
};

const TradingSkillEditor: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'learnings' | 'legacy'>('learnings');

  // ─── Learnings tab state ──────────────────────────────────────
  const [filters, setFilters] = useState<LearningFilters>({
    limit: PAGE_SIZE,
    offset: 0,
  });
  const { data: learningsData, isLoading } = useLearnings(filters);
  const createMutation = useCreateLearning();
  const updateMutation = useUpdateLearning();
  const deleteMutation = useDeleteLearning();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    content: '',
    level: 'user',
    domain: '',
    symbol: '',
    strategy: '',
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    content: '',
    level: '',
    domain: '',
    symbol: '',
    strategy: '',
  });

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ─── Legacy tab state ─────────────────────────────────────────
  const { data: skills = [] } = useTradingSkills();
  const [selectedSkill, setSelectedSkill] = useState<string>('');
  const [baseContent, setBaseContent] = useState<SkillContent | null>(null);
  const [legacyTab, setLegacyTab] = useState<'base' | 'learned'>('learned');
  const [learnedContent, setLearnedContent] = useState<SkillContent | null>(null);

  React.useEffect(() => {
    if (skills.length > 0 && !selectedSkill) {
      setSelectedSkill(skills[0]);
    }
  }, [skills, selectedSkill]);

  React.useEffect(() => {
    if (!selectedSkill || activeTab !== 'legacy') return;
    const load = async () => {
      try {
        const [base, learned] = await Promise.all([
          tradingApi.getSkillBase(selectedSkill),
          tradingApi.getSkillLearned(selectedSkill),
        ]);
        setBaseContent(base);
        setLearnedContent(learned);
      } catch (err) {
        console.error('Failed to load skills:', err);
      }
    };
    load();
  }, [selectedSkill, activeTab]);

  // ─── Handlers ─────────────────────────────────────────────────
  const handleCreate = () => {
    createMutation.mutate(
      {
        content: createForm.content,
        level: createForm.level,
        domain: createForm.domain || undefined,
        symbol: createForm.symbol || undefined,
        strategy: createForm.strategy || undefined,
      },
      {
        onSuccess: () => {
          setShowCreateForm(false);
          setCreateForm({ content: '', level: 'user', domain: '', symbol: '', strategy: '' });
        },
      }
    );
  };

  const startEdit = (learning: ExpertLearning) => {
    setEditingId(learning.id);
    setEditForm({
      content: learning.content,
      level: learning.level,
      domain: learning.domain || '',
      symbol: learning.symbol || '',
      strategy: learning.strategy || '',
    });
  };

  const handleUpdate = () => {
    if (editingId === null) return;
    updateMutation.mutate(
      {
        id: editingId,
        data: {
          content: editForm.content,
          level: editForm.level,
          domain: editForm.domain || undefined,
          symbol: editForm.symbol || undefined,
          strategy: editForm.strategy || undefined,
        },
      },
      {
        onSuccess: () => setEditingId(null),
      }
    );
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: () => setDeleteConfirmId(null),
    });
  };

  const setFilter = (key: keyof LearningFilters, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, offset: 0 }));
  };

  const totalPages = learningsData ? Math.ceil(learningsData.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor((filters.offset || 0) / PAGE_SIZE) + 1;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/trading')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Knowledge Base</h1>
            <p className="text-slate-400 text-sm">Manage Expert learnings and skill files</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700/50 pb-2">
        <button
          onClick={() => setActiveTab('learnings')}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t-lg transition-colors ${
            activeTab === 'learnings' ? 'bg-slate-800 text-teal-400' : 'text-slate-400 hover:text-white'
          }`}
        >
          <Brain size={16} /> Learnings (DB)
        </button>
        <button
          onClick={() => setActiveTab('legacy')}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t-lg transition-colors ${
            activeTab === 'legacy' ? 'bg-slate-800 text-teal-400' : 'text-slate-400 hover:text-white'
          }`}
        >
          <BookOpen size={16} /> Legacy Files (read-only)
        </button>
      </div>

      {activeTab === 'learnings' ? (
        <>
          {/* Filter Bar */}
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={filters.level || ''}
              onChange={(e) => setFilter('level', e.target.value)}
              className="bg-slate-800 border border-slate-700 text-sm text-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-teal-500"
            >
              <option value="">All Levels</option>
              <option value="user">User</option>
              <option value="domain">Domain</option>
              <option value="symbol">Symbol</option>
              <option value="strategy">Strategy</option>
            </select>

            <input
              type="text"
              placeholder="Domain..."
              value={filters.domain || ''}
              onChange={(e) => setFilter('domain', e.target.value)}
              className="bg-slate-800 border border-slate-700 text-sm text-slate-300 rounded-lg px-3 py-1.5 w-32 focus:outline-none focus:border-teal-500"
            />

            <select
              value={filters.source || ''}
              onChange={(e) => setFilter('source', e.target.value)}
              className="bg-slate-800 border border-slate-700 text-sm text-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-teal-500"
            >
              <option value="">All Sources</option>
              <option value="trade_review">Trade Review</option>
              <option value="chat">Chat</option>
              <option value="strategy_health">Strategy Health</option>
              <option value="contra">Contra</option>
              <option value="manual">Manual</option>
            </select>

            <select
              value={filters.status || ''}
              onChange={(e) => setFilter('status', e.target.value)}
              className="bg-slate-800 border border-slate-700 text-sm text-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-teal-500"
            >
              <option value="">All Statuses</option>
              <option value="auto_approved">Auto Approved</option>
              <option value="admin_approved">Admin Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>

            <div className="ml-auto">
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-1.5 bg-teal-500/20 text-teal-400 text-sm rounded-lg hover:bg-teal-500/30 transition-colors"
              >
                <Plus size={16} /> Add Learning
              </button>
            </div>
          </div>

          {/* Create Form Modal */}
          {showCreateForm && (
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-medium text-white">Create Manual Learning</h3>
              <textarea
                placeholder="Learning content..."
                value={createForm.content}
                onChange={(e) => setCreateForm((p) => ({ ...p, content: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white h-20 resize-none focus:outline-none focus:border-teal-500"
              />
              <div className="flex flex-wrap gap-3">
                <select
                  value={createForm.level}
                  onChange={(e) => setCreateForm((p) => ({ ...p, level: e.target.value }))}
                  className="bg-slate-900 border border-slate-700 text-sm text-slate-300 rounded px-2 py-1"
                >
                  <option value="user">User</option>
                  <option value="domain">Domain</option>
                  <option value="symbol">Symbol</option>
                  <option value="strategy">Strategy</option>
                </select>
                <input
                  type="text"
                  placeholder="Domain"
                  value={createForm.domain}
                  onChange={(e) => setCreateForm((p) => ({ ...p, domain: e.target.value }))}
                  className="bg-slate-900 border border-slate-700 text-sm text-slate-300 rounded px-2 py-1 w-28"
                />
                <input
                  type="text"
                  placeholder="Symbol"
                  value={createForm.symbol}
                  onChange={(e) => setCreateForm((p) => ({ ...p, symbol: e.target.value }))}
                  className="bg-slate-900 border border-slate-700 text-sm text-slate-300 rounded px-2 py-1 w-28"
                />
                <input
                  type="text"
                  placeholder="Strategy"
                  value={createForm.strategy}
                  onChange={(e) => setCreateForm((p) => ({ ...p, strategy: e.target.value }))}
                  className="bg-slate-900 border border-slate-700 text-sm text-slate-300 rounded px-2 py-1 w-28"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={!createForm.content.trim() || createMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 bg-teal-500/20 text-teal-400 text-sm rounded hover:bg-teal-500/30 disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Create
                </button>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-slate-300 text-sm rounded hover:bg-slate-600 transition-colors"
                >
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Learnings List */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin mx-auto" />
            </div>
          ) : !learningsData || learningsData.items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Brain size={32} className="mx-auto mb-3" />
              <p>No learnings found. Create one or adjust filters.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {learningsData.items.map((learning) => (
                <div
                  key={learning.id}
                  className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-3"
                >
                  {editingId === learning.id ? (
                    /* Edit mode */
                    <div className="space-y-3">
                      <textarea
                        value={editForm.content}
                        onChange={(e) => setEditForm((p) => ({ ...p, content: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-sm text-white h-20 resize-none focus:outline-none focus:border-teal-500"
                      />
                      <div className="flex flex-wrap gap-2">
                        <select
                          value={editForm.level}
                          onChange={(e) => setEditForm((p) => ({ ...p, level: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1"
                        >
                          <option value="user">User</option>
                          <option value="domain">Domain</option>
                          <option value="symbol">Symbol</option>
                          <option value="strategy">Strategy</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Domain"
                          value={editForm.domain}
                          onChange={(e) => setEditForm((p) => ({ ...p, domain: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1 w-24"
                        />
                        <input
                          type="text"
                          placeholder="Symbol"
                          value={editForm.symbol}
                          onChange={(e) => setEditForm((p) => ({ ...p, symbol: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1 w-24"
                        />
                        <input
                          type="text"
                          placeholder="Strategy"
                          value={editForm.strategy}
                          onChange={(e) => setEditForm((p) => ({ ...p, strategy: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1 w-24"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleUpdate}
                          disabled={updateMutation.isPending}
                          className="flex items-center gap-1 px-3 py-1 bg-teal-500/20 text-teal-400 text-xs rounded hover:bg-teal-500/30 transition-colors"
                        >
                          {updateMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="flex items-center gap-1 px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded hover:bg-slate-600 transition-colors"
                        >
                          <X size={12} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${levelBadge[learning.level] || 'bg-slate-700 text-slate-300'}`}>
                            {learning.level}
                          </span>
                          {learning.domain && (
                            <span className="text-xs bg-indigo-500/10 text-indigo-400 px-2 py-0.5 rounded">
                              {learning.domain}
                            </span>
                          )}
                          {learning.symbol && (
                            <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                              {learning.symbol}
                            </span>
                          )}
                          {learning.strategy && (
                            <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded">
                              {learning.strategy}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${sourceBadge[learning.source] || 'bg-slate-700 text-slate-300'}`}>
                            {learning.source.replace(/_/g, ' ')}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${statusBadge[learning.status] || 'bg-slate-700 text-slate-300'}`}>
                            {learning.status.replace(/_/g, ' ')}
                          </span>
                          {learning.confidence_score != null && (
                            <span className="text-xs text-slate-500">{learning.confidence_score}%</span>
                          )}
                          {(learning.supporting_trades ?? 0) > 0 && (
                            <span className="text-xs text-slate-500">
                              {learning.supporting_trades} trade{learning.supporting_trades !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => startEdit(learning)}
                            className="p-1 text-slate-400 hover:text-teal-400 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          {deleteConfirmId === learning.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(learning.id)}
                                disabled={deleteMutation.isPending}
                                className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded hover:bg-red-500/30"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded hover:bg-slate-600"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(learning.id)}
                              className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      <p
                        className={`text-sm text-slate-300 mt-2 ${
                          expandedId === learning.id ? '' : 'line-clamp-2'
                        } cursor-pointer`}
                        onClick={() => setExpandedId(expandedId === learning.id ? null : learning.id)}
                      >
                        {learning.content}
                      </p>

                      <p className="text-xs text-slate-600 mt-1.5">
                        #{learning.id}
                        {learning.created_at && (
                          <> &middot; {new Date(learning.created_at.endsWith('Z') ? learning.created_at : learning.created_at + 'Z').toLocaleString()}</>
                        )}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setFilters((p) => ({ ...p, offset: Math.max(0, (p.offset || 0) - PAGE_SIZE) }))}
                disabled={currentPage <= 1}
                className="p-1 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm text-slate-400">
                Page {currentPage} of {totalPages} ({learningsData?.total} total)
              </span>
              <button
                onClick={() => setFilters((p) => ({ ...p, offset: (p.offset || 0) + PAGE_SIZE }))}
                disabled={currentPage >= totalPages}
                className="p-1 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      ) : (
        /* Legacy Files tab (read-only) */
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm text-slate-400">Skill Directory:</label>
            <div className="flex gap-2">
              {skills.map((skill) => (
                <button
                  key={skill}
                  onClick={() => setSelectedSkill(skill)}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                    selectedSkill === skill
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setLegacyTab('learned')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                legacyTab === 'learned' ? 'bg-slate-800 text-teal-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              SKILL_LEARNED.md
            </button>
            <button
              onClick={() => setLegacyTab('base')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                legacyTab === 'base' ? 'bg-slate-800 text-teal-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              SKILL.md
            </button>
          </div>

          <div className="w-full h-[400px] bg-slate-900 border border-slate-700 rounded-xl p-4 text-sm text-slate-400 font-mono overflow-y-auto whitespace-pre-wrap">
            {legacyTab === 'base'
              ? baseContent?.content || 'Loading...'
              : learnedContent?.content || 'Loading...'}
          </div>
          {(legacyTab === 'base' ? baseContent : learnedContent)?.last_modified && (
            <p className="text-xs text-slate-500">
              Last modified:{' '}
              {new Date(
                ((legacyTab === 'base' ? baseContent : learnedContent)?.last_modified || '').endsWith('Z')
                  ? (legacyTab === 'base' ? baseContent : learnedContent)!.last_modified!
                  : (legacyTab === 'base' ? baseContent : learnedContent)!.last_modified! + 'Z'
              ).toLocaleString()}
            </p>
          )}
          <p className="text-xs text-amber-400/70">
            Legacy skill files are read-only. Active learnings are managed in the Learnings (DB) tab.
          </p>
        </div>
      )}
    </div>
  );
};

export default TradingSkillEditor;
